/**
 * Las notas agregadas a un informe ya firmado.
 *
 * POR QUE ESTA SEPARADO DEL INFORME
 * =================================
 * Lo que se firmó aquel día y lo que se añadió después no se mezclan nunca.
 * Si se mezclaran, el informe parecería haber dicho desde el principio algo
 * que no dijo, y en una historia clínica eso no es un detalle de formato.
 *
 * La enmienda, que ya existía, hace lo contrario: reescribe el informe y
 * emite una versión nueva que sustituye a la anterior. El médico elige cuál
 * de los dos actos está haciendo.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FilePlus, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { agregarAddendum, getAddenda, type Addendum } from '@/application/reports/report.service'
import { toast } from '@/lib/toast'

interface AddendaProps {
  reportId: string
  /** Solo sobre un informe firmado tiene sentido agregar. */
  sePuedeAgregar: boolean
}

const fechaLegible = (iso: string): string => {
  try {
    return format(new Date(iso), "d 'de' MMMM 'de' yyyy, HH:mm", { locale: es })
  } catch {
    return iso
  }
}

export function Addenda({ reportId, sePuedeAgregar }: AddendaProps) {
  const queryClient = useQueryClient()
  const [escribiendo, setEscribiendo] = useState(false)
  const [texto, setTexto] = useState('')

  const { data: addenda = [], isLoading } = useQuery({
    queryKey: ['reports', reportId, 'addenda'],
    queryFn: () => getAddenda(reportId),
  })

  const agregar = useMutation({
    mutationFn: (contenido: string) => agregarAddendum(reportId, contenido),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports', reportId, 'addenda'] })
      setTexto('')
      setEscribiendo(false)
      toast.success('Addendum agregado y firmado')
    },
    onError: (error: unknown) =>
      toast.error(
        error instanceof Error ? error.message : 'No se pudo agregar el addendum'
      ),
  })

  // Sin notas y sin poder agregarlas, la tarjeta no aporta nada.
  if (!sePuedeAgregar && addenda.length === 0 && !isLoading) return null

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div>
          <CardTitle>Addendum</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Notas agregadas después de firmar. El informe original no se modifica.
          </p>
        </div>
        {sePuedeAgregar && !escribiendo && (
          <Button variant="outline" size="sm" onClick={() => setEscribiendo(true)}>
            <FilePlus className="h-4 w-4 mr-2" />
            Agregar addendum
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {escribiendo && (
          <div className="space-y-3">
            <textarea
              autoFocus
              rows={4}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Escriba la nota que quiere agregar al informe firmado..."
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <p className="text-xs text-muted-foreground">
              Quedará firmado a su nombre y con la fecha de hoy. Para corregir lo que
              el informe dice, use Enmendar en su lugar.
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={!texto.trim() || agregar.isPending}
                onClick={() => agregar.mutate(texto)}
              >
                {agregar.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Firmar y agregar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={agregar.isPending}
                onClick={() => {
                  setEscribiendo(false)
                  setTexto('')
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {isLoading && (
          <p className="text-sm text-muted-foreground">Cargando notas...</p>
        )}

        {!isLoading && addenda.length === 0 && !escribiendo && (
          <p className="text-sm text-muted-foreground">
            Este informe no tiene notas agregadas.
          </p>
        )}

        {addenda.map((nota: Addendum) => (
          <div key={nota.id} className="border-l-2 border-primary/40 pl-4 py-1">
            <p className="text-xs text-muted-foreground">
              {fechaLegible(nota.createdAt)}
              {nota.firmante?.nombre ? ` · ${nota.firmante.nombre}` : ''}
            </p>
            <p className="mt-1 whitespace-pre-wrap">{nota.content}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
